import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { UserContext } from "../../App";
import axios from "axios";
import DnsOutlinedIcon from "@mui/icons-material/DnsOutlined";
import HistoryIcon from "@mui/icons-material/History";
import SearchIcon from "@mui/icons-material/Search";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopOutlinedIcon from "@mui/icons-material/StopOutlined";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import RefreshIcon from "@mui/icons-material/Refresh";
import LinearProgress from "@mui/material/LinearProgress";
import Chip from "@mui/material/Chip";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import "../Invoice/Invoices.css";
import "./InvoicingProcess.css";

function toDisplayString(v, fallback = "") {
  if (v == null) return fallback;
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  if (Array.isArray(v)) return v.map((x) => toDisplayString(x, "")).filter(Boolean).join(", ");
  try {
    return JSON.stringify(v);
  } catch {
    return fallback;
  }
}

function pick(obj, keys, fallback = "") {
  if (!obj || typeof obj !== "object") return fallback;
  for (let i = 0; i < keys.length; i += 1) {
    const k = keys[i];
    const v = obj[k];
    if (v != null && v !== "") return toDisplayString(v, fallback);
  }
  return fallback;
}

function parseStatus(val) {
  const s = String(val ?? "").toLowerCase();
  if (
    s.includes("inactive") ||
    s.includes("stop") ||
    s === "0" ||
    s === "false" ||
    s === "off"
  ) {
    return "inactive";
  }
  if (
    s === "active" ||
    s.includes(" active") ||
    s.includes("active ") ||
    s.includes("run") ||
    s === "1" ||
    s === "true" ||
    s === "on"
  ) {
    return "active";
  }
  return "inactive";
}

function normalizeAutomation(raw, index) {
  const id =
    pick(raw, ["deployment_id", "id", "automation_id", "unique_id", "pk"]) ||
    `row-${index}`;
  return {
    id: String(id),
    name: pick(raw, ["automation_name", "name", "title", "automation"]),
    tag: pick(raw, ["tags", "tag", "slug", "label", "code"]),
    status: parseStatus(
      pick(
        raw,
        ["status", "run_status", "deployment_status", "is_active"],
        "inactive"
      )
    ),
    state: toDisplayString(
      raw?.state ??
        raw?.run_state ??
        raw?.execution_state ??
        raw?.current_state ??
        ""
    ),
    lastTriggeredAt: toDisplayString(
      raw?.last_triggered_at ??
        raw?.last_trigger_at ??
        raw?.last_triggered ??
        raw?.last_run_at ??
        raw?.last_executed_at ??
        ""
    ),
    raw,
  };
}

function fmtRunTimestamp(s) {
  if (!s) return "-";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function runStatePill(state) {
  const s = String(state ?? "").toUpperCase();
  if (!s) return <span style={{ color: "#94a3b8", fontSize: 12 }}>-</span>;
  if (s === "RUNNING") {
    return (
      <span className="ip-pill running">
        <AutorenewIcon sx={{ fontSize: 12 }} />
        Running
      </span>
    );
  }
  if (s === "COMPLETED" || s === "SUCCESS" || s === "DONE") {
    return (
      <span className="ip-pill success">
        <CheckCircleOutlineIcon sx={{ fontSize: 12 }} />
        {s === "COMPLETED" ? "Completed" : s.charAt(0) + s.slice(1).toLowerCase()}
      </span>
    );
  }
  if (s === "FAILED" || s === "ERROR") {
    return (
      <span className="ip-pill failed">
        <ErrorOutlineIcon sx={{ fontSize: 12 }} />
        {s.charAt(0) + s.slice(1).toLowerCase()}
      </span>
    );
  }
  return (
    <span className="ip-pill" style={{ background: "#f1f5f9", color: "#475569" }}>
      {s.charAt(0) + s.slice(1).toLowerCase()}
    </span>
  );
}

function extractList(res) {
  const d = res?.data;
  if (!d) return [];
  if (Array.isArray(d)) return d;
  if (Array.isArray(d.data)) return d.data;
  if (Array.isArray(d.results)) return d.results;
  if (Array.isArray(d.automations)) return d.automations;
  if (Array.isArray(d.list)) return d.list;
  return [];
}

/** Pull progress / current script info from varied API shapes */
function extractExecutionPayload(payload) {
  const root = payload?.data ?? payload;
  let progress =
    typeof root?.progress === "number"
      ? root.progress
      : typeof root?.progress_percent === "number"
        ? root.progress_percent
        : null;
  if (progress != null && progress <= 1 && progress >= 0) {
    progress = Math.round(progress * 100);
  }
  const currentFile = toDisplayString(
    root?.current_file ??
      root?.current_script ??
      root?.python_file ??
      root?.file ??
      root?.script ??
      ""
  );
  const message = toDisplayString(
    root?.message ?? root?.status_message ?? root?.detail ?? ""
  );
  let files = [];
  if (Array.isArray(root?.files)) files = root.files.map((f) => toDisplayString(f));
  else if (Array.isArray(root?.python_files))
    files = root.python_files.map((f) => toDisplayString(f));
  else if (Array.isArray(root?.steps))
    files = root.steps
      .map((s) =>
        typeof s === "string" ? s : toDisplayString(s?.file || s?.name || s?.script || "")
      )
      .filter(Boolean);
  return { progress, currentFile, message, files };
}

function statusIcon(status) {
  if (status === "success") {
    return <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />;
  }
  if (status === "failed") {
    return <ErrorOutlineIcon sx={{ fontSize: 14 }} />;
  }
  return <AutorenewIcon sx={{ fontSize: 14 }} />;
}

const {
  REACT_APP_GET_INVOICE_AUTOMATION_LIST,
  REACT_APP_POST_INVOICE_AUTOMATION_UPDATE,
  REACT_APP_POST_INVOICE_AUTOMATION_TRIGGER,
  REACT_APP_GET_AUTOMATION_LOGS,
} = process.env;

function InvoicingProcess({ isSidebarOpen, email }) {
  const { handleAccess, setAuthPop } = useContext(UserContext);
  const [view, setView] = useState("master");
  const [query, setQuery] = useState("");
  const [automations, setAutomations] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [historyLogs, setHistoryLogs] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [execById, setExecById] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [pendingAction, setPendingAction] = useState({
    open: false,
    type: "",
    row: null,
  });
  const triggerProgressIntervals = useRef({});
  const execClearTimeouts = useRef({});

  const fetchAutomations = useCallback(async () => {
    if (!REACT_APP_GET_INVOICE_AUTOMATION_LIST) {
      setListError("REACT_APP_GET_INVOICE_AUTOMATION_LIST is not set");
      setListLoading(false);
      setAutomations([]);
      return;
    }
    setListLoading(true);
    setListError("");
    try {
      const res = await axios.get(REACT_APP_GET_INVOICE_AUTOMATION_LIST);
      const rows = extractList(res).map(normalizeAutomation);
      setAutomations(rows);
    } catch (e) {
      setListError(toDisplayString(e?.message) || "Failed to load automations");
      setAutomations([]);
    } finally {
      setListLoading(false);
    }
  }, []);

  const fetchHistoryLogs = useCallback(async () => {
    if (!REACT_APP_GET_AUTOMATION_LOGS) {
      setHistoryError("REACT_APP_GET_AUTOMATION_LOGS is not set");
      setHistoryLoading(false);
      setHistoryLogs([]);
      return;
    }
    setHistoryLoading(true);
    setHistoryError("");
    try {
      const res = await axios.get(REACT_APP_GET_AUTOMATION_LOGS);
      const data = Array.isArray(res?.data) ? res.data : res?.data?.data ?? [];
      setHistoryLogs(Array.isArray(data) ? data : []);
    } catch (e) {
      setHistoryError(toDisplayString(e?.message) || "Failed to load history logs");
      setHistoryLogs([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAutomations();
  }, [fetchAutomations]);

  useEffect(() => {
    if (view === "history") {
      fetchHistoryLogs();
    }
  }, [view, fetchHistoryLogs]);

  // Auto-refresh the currently visible list every 30 s.
  // Pauses when the tab is hidden so we don't waste API calls in background tabs.
  useEffect(() => {
    const tick = () => {
      if (document.visibilityState !== "visible") return;
      if (view === "history") {
        if (!historyLoading) fetchHistoryLogs();
      } else {
        if (!listLoading) fetchAutomations();
      }
    };
    const id = setInterval(tick, 30000);
    const onVisibility = () => {
      if (document.visibilityState === "visible") tick();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [view, fetchAutomations, fetchHistoryLogs, listLoading, historyLoading]);

  const activeCount = useMemo(
    () => automations.filter((r) => r.status === "active").length,
    [automations]
  );
  const inactiveCount = useMemo(
    () => automations.filter((r) => r.status === "inactive").length,
    [automations]
  );

  const q = query.trim().toLowerCase();

  const masterFiltered = useMemo(() => {
    if (!q) return automations;
    return automations.filter(
      (r) =>
        String(r.name).toLowerCase().includes(q) ||
        String(r.tag).toLowerCase().includes(q)
    );
  }, [automations, q]);

  const historyFiltered = useMemo(() => {
    if (!q) return historyLogs;
    return historyLogs.filter(
      (r) =>
        toDisplayString(r.deployment_name).toLowerCase().includes(q) ||
        toDisplayString(r.status).toLowerCase().includes(q) ||
        toDisplayString(r.updated_by).toLowerCase().includes(q) ||
        toDisplayString(r.updated_date).toLowerCase().includes(q)
    );
  }, [historyLogs, q]);

  const showSnackbar = (message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  };

  const executeAutomationAction = async (type, row) => {
    const urlMap = {
      start: REACT_APP_POST_INVOICE_AUTOMATION_UPDATE,
      stop: REACT_APP_POST_INVOICE_AUTOMATION_UPDATE,
      trigger: REACT_APP_POST_INVOICE_AUTOMATION_TRIGGER,
    };
    const url = urlMap[type];
    const envKey =
      type === "trigger"
        ? "REACT_APP_POST_INVOICE_AUTOMATION_TRIGGER"
        : "REACT_APP_POST_INVOICE_AUTOMATION_UPDATE";
    if (!url || !String(url).trim()) {
      showSnackbar(
        `Configure ${envKey} in .env to enable ${type}.`,
        "warning"
      );
      return;
    }

    const id = row.id;
    if (type === "trigger") {
      if (triggerProgressIntervals.current[id]) {
        clearInterval(triggerProgressIntervals.current[id]);
      }
      if (execClearTimeouts.current[id]) {
        clearTimeout(execClearTimeouts.current[id]);
        delete execClearTimeouts.current[id];
      }
      setExecById((prev) => ({
        ...prev,
        [id]: {
          phase: "running",
          indeterminate: false,
          progress: 6,
          currentFile: "",
          message: "Running Python scripts…",
          files: [],
        },
      }));
      triggerProgressIntervals.current[id] = setInterval(() => {
        setExecById((prev) => {
          const curr = prev[id];
          if (!curr || curr.phase !== "running") return prev;
          const currentProgress = Number(curr.progress ?? 0);
          const nextProgress = Math.min(90, currentProgress + 4);
          return {
            ...prev,
            [id]: {
              ...curr,
              progress: nextProgress,
            },
          };
        });
      }, 1200);
    }

    try {
      const body =
        type === "trigger"
          ? {
              automation_id: id,
              automationId: id,
              id,
              automation_name: row?.raw?.automation_name ?? row.name,
              action: type,
              updated_by: email,
            }
          : {
              deployment_id: id,
              automation_name: row?.raw?.automation_name ?? row.name,
              status: type === "start" ? "start" : "pause",
              updated_by: email,
            };
      const res = await axios.post(url, body, { timeout: 300000 });

      if (type === "trigger") {
        if (triggerProgressIntervals.current[id]) {
          clearInterval(triggerProgressIntervals.current[id]);
          delete triggerProgressIntervals.current[id];
        }
        const ex = extractExecutionPayload(res.data);
        const pct =
          ex.progress != null ? Math.min(100, Math.max(0, ex.progress)) : 100;
        setExecById((prev) => ({
          ...prev,
          [id]: {
            phase: "done",
            indeterminate: false,
            progress: pct,
            currentFile: ex.currentFile,
            message: ex.message || "Completed",
            files: ex.files,
          },
        }));
        execClearTimeouts.current[id] = setTimeout(() => {
          delete execClearTimeouts.current[id];
          setExecById((prev) => {
            const next = { ...prev };
            delete next[id];
            return next;
          });
        }, 8000);
      } else {
        showSnackbar(
          type === "start" ? "Start requested." : "Stop requested.",
          "success"
        );
      }
      fetchAutomations();
    } catch (e) {
      const msg =
        toDisplayString(e?.response?.data?.message) ||
        toDisplayString(e?.response?.data?.detail) ||
        toDisplayString(e?.message) ||
        "Request failed";
      if (type === "trigger") {
        if (triggerProgressIntervals.current[id]) {
          clearInterval(triggerProgressIntervals.current[id]);
          delete triggerProgressIntervals.current[id];
        }
        setExecById((prev) => ({
          ...prev,
          [id]: {
            phase: "error",
            indeterminate: false,
            progress: null,
            currentFile: "",
            message: msg,
            files: [],
          },
        }));
      }
      showSnackbar(msg, "error");
    }
  };

  const requestAutomationAction = (type, row) => {
    const actionMap = { start: "Start", stop: "Stop", trigger: "Trigger" };
    const requiredAction = actionMap[type];
    if (requiredAction && !handleAccess("InvoicingProcess", requiredAction)) {
      setAuthPop(true);
      return;
    }
    setPendingAction({ open: true, type, row });
  };

  const closeConfirmationDialog = () => {
    setPendingAction({ open: false, type: "", row: null });
  };

  const confirmAutomationAction = async () => {
    const { type, row } = pendingAction;
    closeConfirmationDialog();
    if (!type || !row) return;
    await executeAutomationAction(type, row);
  };

  const actionLabel = (type) => {
    if (type === "start") return "Start";
    if (type === "stop") return "Pause";
    if (type === "trigger") return "Trigger";
    return "Proceed";
  };

  useEffect(() => {
    return () => {
      Object.values(triggerProgressIntervals.current).forEach((timer) =>
        clearInterval(timer)
      );
      triggerProgressIntervals.current = {};
      Object.values(execClearTimeouts.current).forEach((timer) =>
        clearTimeout(timer)
      );
      execClearTimeouts.current = {};
    };
  }, []);

  return (
    <div className={`${isSidebarOpen ? "invoices-main" : "invoices-min"}`}>
      <div className={`${isSidebarOpen ? "invoice-div" : "invoice-div-min"}`}>
        <div className="invoice-main-tabs" role="tablist">
          <div
            role="tab"
            aria-selected={view === "master"}
            className={`invoice-main-tab-item ip-tab-with-icon ${view === "master" ? "invoice-main-tab-active" : ""}`}
            onClick={() => setView("master")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") setView("master");
            }}
            tabIndex={0}
          >
            <DnsOutlinedIcon sx={{ fontSize: 18 }} />
            Master
          </div>
          <div
            role="tab"
            aria-selected={view === "history"}
            className={`invoice-main-tab-item ip-tab-with-icon ${view === "history" ? "invoice-main-tab-active" : ""}`}
            onClick={() => {
              if (handleAccess("InvoicingProcess", "View History")) {
                setView("history");
              } else {
                setAuthPop(true);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                if (handleAccess("InvoicingProcess", "View History")) {
                  setView("history");
                } else {
                  setAuthPop(true);
                }
              }
            }}
            tabIndex={0}
          >
            <HistoryIcon sx={{ fontSize: 18 }} />
            History
          </div>
        </div>

        <div className="mail-tracker-toolbar" style={{ marginTop: "10px" }}>
          {view === "master" ? (
            <div className="mail-tracker-summary">
              <div className="mail-tracker-card" style={{ backgroundColor: "#ecfdf5" }}>
                <span className="mail-tracker-card-label">Active</span>
                <span className="mail-tracker-card-value" style={{ color: "#16a34a" }}>{activeCount}</span>
              </div>
              <div className="mail-tracker-card" style={{ backgroundColor: "#fef2f2" }}>
                <span className="mail-tracker-card-label">Inactive</span>
                <span className="mail-tracker-card-value" style={{ color: "#dc2626" }}>{inactiveCount}</span>
              </div>
              <div className="mail-tracker-card" style={{ backgroundColor: "#eef2ff" }}>
                <span className="mail-tracker-card-label">Total</span>
                <span className="mail-tracker-card-value" style={{ color: "#3b82f6" }}>{automations.length}</span>
              </div>
            </div>
          ) : (
            <div />
          )}
          <div className="mail-tracker-filters">
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon style={{ fontSize: "16px" }} />}
              onClick={view === "history" ? fetchHistoryLogs : fetchAutomations}
              disabled={view === "history" ? historyLoading : listLoading}
              sx={{
                textTransform: "none",
                borderRadius: "6px",
                fontSize: "11px",
                padding: "4px 10px",
                borderColor: "#3949ab",
                color: "#3949ab",
                "&:hover": { borderColor: "#303f9f", backgroundColor: "#eef2ff" },
              }}
            >
              Refresh
            </Button>
            <div className="ip-search">
              <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
              <input
                type="search"
                placeholder={
                  view === "master"
                    ? "Search by automation name or tag…"
                    : "Search history…"
                }
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label="Search"
              />
            </div>
          </div>
        </div>

        {view === "master" && (
          <div className="ip-table-wrap">
            <table className="ip-table">
              <thead>
                <tr>
                  <th>Automation Name</th>
                  <th>Tag</th>
                  <th>Status</th>
                  <th>State</th>
                  <th>Last Triggered At</th>
                  <th style={{ textAlign: "center" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {listLoading ? (
                  <tr>
                    <td colSpan={6} className="ip-empty">Loading automations…</td>
                  </tr>
                ) : listError ? (
                  <tr>
                    <td colSpan={6} className="ip-empty ip-error-text">{listError}</td>
                  </tr>
                ) : masterFiltered.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="ip-empty">No automations match your search.</td>
                  </tr>
                ) : (
                  masterFiltered.map((row) => (
                    <React.Fragment key={row.id}>
                      <tr>
                        <td style={{ fontWeight: 600 }}>{row.name || "-"}</td>
                        <td>
                          {row.tag ? (
                            <Chip label={row.tag} size="small" className="ip-tag-chip" />
                          ) : (
                            <span style={{ color: "#94a3b8" }}>No tag</span>
                          )}
                        </td>
                        <td>
                          <span className={`ip-pill ${row.status === "active" ? "success" : "failed"}`}>
                            <span className={`ip-status-dot ${row.status === "active" ? "green" : "red"}`} />
                            {row.status === "active" ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td>{runStatePill(row.state)}</td>
                        <td style={{ fontSize: 12, color: "#475569", whiteSpace: "nowrap" }}>
                          {fmtRunTimestamp(row.lastTriggeredAt)}
                        </td>
                        <td>
                          <div className="ip-card-actions" style={{ justifyContent: "center" }}>
                            <button
                              type="button"
                              className="ip-action-btn start"
                              disabled={row.status === "active"}
                              onClick={() => requestAutomationAction("start", row)}
                            >
                              <PlayArrowIcon sx={{ fontSize: 16 }} />
                              Start
                            </button>
                            <button
                              type="button"
                              className="ip-action-btn stop"
                              disabled={
                                row.status === "inactive" ||
                                execById[row.id]?.phase === "running" ||
                                String(row.state ?? "").toUpperCase() === "RUNNING"
                              }
                              onClick={() => requestAutomationAction("stop", row)}
                            >
                              <StopOutlinedIcon sx={{ fontSize: 16 }} />
                              Stop
                            </button>
                            <button
                              type="button"
                              className="ip-action-btn trigger"
                              disabled={
                                execById[row.id]?.phase === "running" ||
                                String(row.state ?? "").toUpperCase() === "RUNNING"
                              }
                              onClick={() => requestAutomationAction("trigger", row)}
                            >
                              <FlashOnIcon sx={{ fontSize: 16 }} />
                              Trigger
                            </button>
                          </div>
                        </td>
                      </tr>
                      {execById[row.id] && (
                        <tr>
                          <td colSpan={6} style={{ padding: 0 }}>
                            <div
                              className={`ip-exec-panel ${execById[row.id].phase === "error" ? "ip-exec-error" : ""}`}
                            >
                              <div className="ip-exec-heading">
                                Python execution
                                {execById[row.id].phase === "done" && (
                                  <span className="ip-exec-done">Done</span>
                                )}
                                {execById[row.id].phase === "error" && (
                                  <span className="ip-exec-err">Failed</span>
                                )}
                              </div>
                              {(execById[row.id].phase === "running" ||
                                execById[row.id].progress != null) && (
                                <LinearProgress
                                  className="ip-linear-progress"
                                  variant={
                                    execById[row.id].indeterminate
                                      ? "indeterminate"
                                      : "determinate"
                                  }
                                  value={execById[row.id].progress ?? 0}
                                  sx={{
                                    height: 8,
                                    borderRadius: 1,
                                    bgcolor: "#e2e8f0",
                                    "& .MuiLinearProgress-bar": {
                                      backgroundColor: "#3c48ad",
                                    },
                                  }}
                                />
                              )}
                              {execById[row.id].currentFile && (
                                <div className="ip-exec-file mono">
                                  Current: {execById[row.id].currentFile}
                                </div>
                              )}
                              {execById[row.id].message && (
                                <div className="ip-exec-msg">
                                  {execById[row.id].message}
                                </div>
                              )}
                              {execById[row.id].files?.length > 0 && (
                                <ul className="ip-exec-files mono">
                                  {execById[row.id].files.map((f, i) => (
                                    <li key={i}>{f}</li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {view === "history" && (
          <div className="ip-table-wrap">
            <table className="ip-table">
              <thead>
                <tr>
                  <th>Deployment Name</th>
                  <th>Status</th>
                  <th>Updated By</th>
                  <th>Updated Date</th>
                </tr>
              </thead>
              <tbody>
                {historyLoading ? (
                  <tr>
                    <td colSpan={4} className="ip-empty">Loading history…</td>
                  </tr>
                ) : historyError ? (
                  <tr>
                    <td colSpan={4} className="ip-empty ip-error-text">{historyError}</td>
                  </tr>
                ) : historyFiltered.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="ip-empty">
                      No history rows match your search.
                    </td>
                  </tr>
                ) : (
                  historyFiltered.map((row, i) => {
                    const statusStr = toDisplayString(row.status, "");
                    const s = statusStr.toLowerCase();
                    const pillClass =
                      s === "triggered"
                        ? "triggered"
                        : s === "start"
                          ? "success"
                          : s === "pause" || s === "stop" || s === "failed"
                            ? "failed"
                            : "running";
                    return (
                      <tr key={i}>
                        <td>{toDisplayString(row.deployment_name, "-") || "-"}</td>
                        <td>
                          <span className={`ip-pill ${pillClass}`}>
                            {statusIcon(pillClass)}
                            {statusStr || "-"}
                          </span>
                        </td>
                        <td>{toDisplayString(row.updated_by, "-") || "-"}</td>
                        <td>
                          {(() => {
                            if (!row.updated_date) return "-";
                            const d = new Date(row.updated_date);
                            if (Number.isNaN(d.getTime())) return "-";
                            return d.toLocaleString("en-US", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: true,
                            });
                          })()}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={pendingAction.open}
        onClose={closeConfirmationDialog}
        aria-labelledby="automation-confirmation-title"
      >
        <DialogTitle id="automation-confirmation-title">
          Confirm {actionLabel(pendingAction.type)}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pendingAction.type === "start" &&
              `Are you sure you want to start deployment "${pendingAction.row?.name || pendingAction.row?.id || "-"}"?`}
            {pendingAction.type === "stop" &&
              `Are you sure you want to pause deployment "${pendingAction.row?.name || pendingAction.row?.id || "-"}"?`}
            {pendingAction.type === "trigger" &&
              `Are you sure you want to trigger deployment "${pendingAction.row?.name || pendingAction.row?.id || "-"}"?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmationDialog} color="inherit">
            Cancel
          </Button>
          <Button onClick={confirmAutomationAction} variant="contained">
            {actionLabel(pendingAction.type)}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default InvoicingProcess;
